/**
 * useExpenseEntries Hook
 * Custom hook for fetching, creating, updating, and deleting expense entries using TanStack Query.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { transactionApi } from '../api/transactionApi';
import type { TransactionWithCategory, SupportedCurrency, CurrencySnapshot } from '../types';
import { calculateTotalInCurrency } from '../types';
import { useUIStore } from '../store/uiStore';
import { monthlyExpensesKeys } from './useMonthlyExpenses';
import { reportKeys } from './useYearlyReport';

export const expenseEntriesKeys = {
  all: ['expenseEntries'] as const,
  month: (year: number, month: number) => [...expenseEntriesKeys.all, year, month] as const,
};

export interface ExpenseEntryRecord {
  id: string;
  amount: number; // Amount in BRL
  description: string;
  date: string;
  currency: string; // Original input currency
  currencySnapshots: CurrencySnapshot; // Exchange rates at entry time
  categoryId: string;
  categoryName: string;
  categoryNameKey: string;
  categoryColor: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Hook for fetching expense entries for a specific month
 * @param year Year
 * @param month Month (1-12)
 * @param targetCurrency Optional target currency for total calculation (default: BRL)
 */
export function useExpenseEntries(
  year: number,
  month: number,
  targetCurrency: SupportedCurrency = 'BRL'
) {
  const startDate = new Date(year, month - 1, 1);
  const endDate = new Date(year, month, 0, 23, 59, 59, 999);

  const query = useQuery({
    queryKey: expenseEntriesKeys.month(year, month),
    queryFn: () =>
      transactionApi.getTransactions({
        type: 'EXPENSE',
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        limit: 100,
        offset: 0,
      }),
    staleTime: 1 * 60 * 1000, // 1 minute
    retry: false,
  });

  const entries: ExpenseEntryRecord[] = (query.data?.data ?? []).map((t: TransactionWithCategory) => ({
    id: t.id,
    amount: t.amount, // Amount in BRL
    description: t.description,
    date: t.date,
    currency: t.currency || 'BRL',
    currencySnapshots: t.currencySnapshots || { BRL: 1 },
    categoryId: t.categoryId,
    categoryName: t.category.name,
    categoryNameKey: t.category.nameKey || '',
    categoryColor: t.category.color,
    createdAt: t.createdAt,
    updatedAt: t.updatedAt,
  }));

  // Calculate total in the target currency using stored currency snapshots from each entry
  // Formula: DisplayValue = amountBRL * currencySnapshots[targetCurrency]
  const total = calculateTotalInCurrency(entries, targetCurrency);

  return {
    entries,
    total,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Hook for updating an expense entry
 */
export function useUpdateExpenseEntry() {
  const queryClient = useQueryClient();
  const { addToast } = useUIStore();

  return useMutation({
    mutationFn: ({
      entryId,
      data,
    }: {
      entryId: string;
      data: { amount?: number; description?: string; date?: string; categoryId?: string };
    }) => transactionApi.updateTransaction(entryId, data),
    onSuccess: () => {
      // Invalidate all related queries for global state sync:
      // - Expense entries list (BudgetPage history)
      // - Monthly expenses (Profile page "Gasto Este Mês")
      // - Reports (Expenses, Balance)
      // - Home page donut chart (expenses by category)
      queryClient.invalidateQueries({ queryKey: expenseEntriesKeys.all });
      queryClient.invalidateQueries({ queryKey: monthlyExpensesKeys.all });
      queryClient.invalidateQueries({ queryKey: reportKeys.all });
      addToast('success', 'Despesa atualizada com sucesso!');
    },
    onError: (error: Error) => {
      addToast('error', error.message || 'Erro ao atualizar despesa');
    },
  });
}

/**
 * Hook for deleting an expense entry
 */
export function useDeleteExpenseEntry() {
  const queryClient = useQueryClient();
  const { addToast } = useUIStore();

  return useMutation({
    mutationFn: (entryId: string) => transactionApi.deleteTransaction(entryId),
    onSuccess: () => {
      // Invalidate all related queries for global state sync
      queryClient.invalidateQueries({ queryKey: expenseEntriesKeys.all });
      queryClient.invalidateQueries({ queryKey: monthlyExpensesKeys.all });
      queryClient.invalidateQueries({ queryKey: reportKeys.all });
      addToast('success', 'Despesa excluída com sucesso!');
    },
    onError: (error: Error) => {
      addToast('error', error.message || 'Erro ao excluir despesa');
    },
  });
}
